/**
 * Post-based project scoping — client-side "which post is active" storage.
 *
 * A user can hold more than one post (e.g. GM for two regions), each with
 * its own project set (see the `posts` field returned by /auth/me and the
 * SSO/login responses). The chosen post id is remembered per-browser in
 * localStorage and sent back to the server as the X-Post-Id header (see
 * services/api.js) on every request, so post-scoped filtering can be
 * resolved server-side from the user's own `posts` list.
 */

const STORAGE_KEY = 'activePostId';

export const getStoredPostId = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

export const setStoredPostId = (postId) => {
  try {
    if (postId) {
      localStorage.setItem(STORAGE_KEY, postId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors (private browsing, quota, etc.)
  }
};

export const clearStoredPostId = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
};

/**
 * Finds one post by id within a user's post list.
 */
export const findPost = (user, postId) => {
  if (!user?.posts?.length || !postId) return null;
  return user.posts.find((p) => p.postId === String(postId)) || null;
};

/**
 * Resolves which post should be active right after login/refresh:
 * - No posts at all -> clear storage, null.
 * - A previously-stored id that's still one of this user's posts -> keep it.
 * - Otherwise the server's resolved activePostId / user's mainPostId.
 * - Otherwise the first post.
 * The resolved id is (re-)persisted so it survives a reload.
 */
export const resolveInitialPostId = (user) => {
  if (!user?.posts?.length) {
    clearStoredPostId();
    return null;
  }

  const storedId = getStoredPostId();
  if (storedId && findPost(user, storedId)) {
    return storedId;
  }

  const fallbackId =
    (user.activePostId && findPost(user, user.activePostId) && user.activePostId) ||
    (user.mainPostId && findPost(user, user.mainPostId) && user.mainPostId) ||
    user.posts[0].postId;

  setStoredPostId(fallbackId);
  return fallbackId;
};

/**
 * True when the given post exists but has no projects assigned to it.
 */
export const activePostHasNoProjects = (user, postId) => {
  const post = findPost(user, postId);
  return !!post && (!post.projects || post.projects.length === 0);
};

/**
 * Strips a trailing " - <number>" from a post short name for display (e.g.
 * "ED - NR - 3" -> "ED - NR"). Matches the backend's post-scoping match
 * (see buildProjectScopeMatch in routes/queries.js), which ignores that same
 * suffix because AAI's own systems disagree on whether it's present - the
 * SSO login's postdetails claim includes it, but GetProjectAssignee (used
 * when a query is raised) never does.
 */
export const formatPostShortName = (postShortName) => {
  if (typeof postShortName !== 'string') return postShortName;
  return postShortName.replace(/\s*-\s*\d+\s*$/, '').trim();
};

/**
 * Resolves the currently active post's short name for base-name comparisons
 * (stripped of its trailing " - <number>" suffix, lowercased) — e.g. gating a
 * feature to a specific post regardless of that numeric suffix. Read-only:
 * unlike resolveInitialPostId, it never touches storage, so it's safe to call
 * from anywhere a user object is available (including outside AuthContext).
 */
export const getActivePostShortNameBase = (user) => {
  if (!user?.posts?.length) return '';

  const storedId = getStoredPostId();
  const postId =
    (storedId && findPost(user, storedId) && storedId) ||
    (user.activePostId && findPost(user, user.activePostId) && user.activePostId) ||
    (user.mainPostId && findPost(user, user.mainPostId) && user.mainPostId) ||
    user.posts[0].postId;

  const post = findPost(user, postId);
  return post?.postShortName
    ? formatPostShortName(post.postShortName).trim().toLowerCase()
    : '';
};
